import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Clock, Play, Square, Users as UsersIcon, Timer, Keyboard, Download } from "lucide-react";
import { roleLabel } from "../lib/format";
import { PrintButton, PrintHeader } from "../components/Print";
import KioskPunchClock from "../components/KioskPunchClock";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "../components/ui/popover";

const fmtDuration = (secs) => {
  if (!secs && secs !== 0) return "—";
  const s = Math.max(0, Math.floor(secs));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const hh = String(h).padStart(2, "0");
  const mm = String(m).padStart(2, "0");
  const ss = String(sec).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
};
const fmtHours = (secs) => {
  const h = (secs || 0) / 3600;
  return h.toFixed(2) + "h";
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function TimeClockPage() {
  const { user, isManager } = useAuth();
  const [status, setStatus] = useState(null);
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [tick, setTick] = useState(0); // to force elapsed re-render
  const [viewUserId, setViewUserId] = useState(user?.id || "");
  const [users, setUsers] = useState([]);
  const [kioskMode, setKioskMode] = useState(() => {
    try { return localStorage.getItem("jc_kiosk_mode") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("jc_kiosk_mode", kioskMode ? "1" : "0"); } catch {}
  }, [kioskMode]);

  // Pay-period CSV picker
  const mondayISO = () => {
    const d = new Date();
    const day = (d.getDay() + 6) % 7; // 0 = Monday
    d.setDate(d.getDate() - day);
    d.setHours(0, 0, 0, 0);
    return d.toISOString().slice(0, 10);
  };
  const [periodStart, setPeriodStart] = useState(mondayISO());
  const [periodWeeks, setPeriodWeeks] = useState(1);
  const [exportBusy, setExportBusy] = useState(false);

  const downloadPayPeriod = async () => {
    setExportBusy(true);
    try {
      const res = await api.get("/time/pay-period.csv", {
        params: { start: periodStart, weeks: periodWeeks },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const disposition = res.headers?.["content-disposition"] || "";
      const match = /filename="?([^";]+)"?/i.exec(disposition);
      a.download = match ? match[1] : `pay-period-${periodStart}-${periodWeeks}w.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Pay period CSV downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed");
    } finally {
      setExportBusy(false);
    }
  };

  const loadAll = async () => {
    const [s, e, sum] = await Promise.all([
      api.get("/time/status"),
      api.get("/time/entries", { params: viewUserId ? { user_id: viewUserId } : {} }),
      api.get("/time/summary"),
    ]);
    setStatus(s.data);
    setEntries(e.data);
    setSummary(sum.data);
  };

  useEffect(() => {
    if (isManager) {
      api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
    }
  }, [isManager]);

  useEffect(() => { loadAll(); }, [viewUserId]);

  // Update elapsed every second while clocked in
  useEffect(() => {
    if (!status?.clocked_in) return;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [status?.clocked_in]);

  const liveElapsed = useMemo(() => {
    if (!status?.clocked_in || !status.entry?.clock_in) return 0;
    const start = new Date(status.entry.clock_in).getTime();
    return Math.floor((Date.now() - start) / 1000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, tick]);

  const doClockIn = async () => {
    setBusy(true);
    try {
      await api.post("/time/clock-in", { note: note.trim() });
      toast.success("Clocked in — have a great shift");
      setNote("");
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clock in failed");
    } finally { setBusy(false); }
  };

  const doClockOut = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/time/clock-out", { note: note.trim() });
      toast.success(`Clocked out — ${fmtHours(data.duration_seconds)} logged`);
      setNote("");
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clock out failed");
    } finally { setBusy(false); }
  };

  const activeEntry = status?.entry;

  const entriesFiltered = entries.filter((e) => (viewUserId ? e.user_id === viewUserId : true));
  const myWeekTotal = summary?.totals?.find((t) => t.user_id === user?.id)?.seconds || 0;

  return (
    <div className="print-container">
      <PageHeader
        eyebrow="Time"
        title="Time Clock"
        description="Clock in when you start work, clock out when you're done. Managers can see the whole team."
        testId="time-header"
        actions={
          <div className="flex items-center gap-2 no-print">
            {isManager && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    data-testid="pay-period-btn"
                    className="rounded-sm border-slate-300"
                  >
                    <Download size={16} className="mr-1.5" /> Pay Period CSV
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-80" data-testid="pay-period-popover">
                  <div className="space-y-3">
                    <div>
                      <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Pay Period</div>
                      <div className="font-display text-lg font-black text-slate-900 mt-0.5">Export hours</div>
                    </div>
                    <div>
                      <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Start date (Monday)</Label>
                      <Input
                        type="date"
                        value={periodStart}
                        onChange={(e) => setPeriodStart(e.target.value)}
                        data-testid="pay-period-start"
                        className="mt-1 rounded-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Length</Label>
                      <div className="mt-1 flex gap-2">
                        <Button
                          type="button"
                          onClick={() => setPeriodWeeks(1)}
                          variant={periodWeeks === 1 ? "default" : "outline"}
                          className={`flex-1 rounded-sm ${periodWeeks === 1 ? "bg-blue-800 hover:bg-blue-900 text-white" : "border-slate-300"}`}
                          data-testid="pay-period-weeks-1"
                        >
                          1 week
                        </Button>
                        <Button
                          type="button"
                          onClick={() => setPeriodWeeks(2)}
                          variant={periodWeeks === 2 ? "default" : "outline"}
                          className={`flex-1 rounded-sm ${periodWeeks === 2 ? "bg-blue-800 hover:bg-blue-900 text-white" : "border-slate-300"}`}
                          data-testid="pay-period-weeks-2"
                        >
                          2 weeks (biweekly)
                        </Button>
                      </div>
                    </div>
                    <Button
                      onClick={downloadPayPeriod}
                      disabled={exportBusy}
                      data-testid="pay-period-download"
                      className="w-full rounded-sm bg-blue-800 hover:bg-blue-900 text-white"
                    >
                      <Download size={16} className="mr-1.5" /> {exportBusy ? "Preparing…" : "Download CSV"}
                    </Button>
                    <div className="text-[10px] text-slate-500 font-mono">
                      Includes clock-in/out rows for every teammate and crew member in the window, with totals and overtime (&gt;40h/wk) at the bottom.
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            )}
            {isManager && (
              <Button
                variant={kioskMode ? "default" : "outline"}
                onClick={() => setKioskMode((v) => !v)}
                data-testid="toggle-kiosk-btn"
                className={`rounded-sm ${kioskMode ? "bg-blue-800 hover:bg-blue-900 text-white" : "border-slate-300"}`}
              >
                <Keyboard size={16} className="mr-1.5" /> {kioskMode ? "Kiosk: ON" : "Kiosk Mode"}
              </Button>
            )}
            <PrintButton testId="time-print-btn" label="Print Hours" />
          </div>
        }
      />

      <div className="p-4 md:p-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <PrintHeader
          title={isManager ? "Team Hours Report" : `${user?.name || ""} — Hours Report`}
          subtitle={`Week starting ${summary?.week_start ? new Date(summary.week_start).toLocaleDateString() : "—"}`}
        />

        {kioskMode && isManager && (
          <div className="lg:col-span-3">
            <KioskPunchClock onPunch={() => loadAll()} />
          </div>
        )}

        {/* Print-only weekly totals table */}
        <div className="print-only lg:col-span-3" style={{ marginBottom: 16 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #0f172a", textAlign: "left" }}>
                <th style={{ padding: "6px 4px" }}>Name</th>
                <th style={{ padding: "6px 4px" }}>Role</th>
                <th style={{ padding: "6px 4px", textAlign: "right" }}>Hours This Week</th>
                <th style={{ padding: "6px 4px", textAlign: "right" }}>Entries</th>
              </tr>
            </thead>
            <tbody>
              {(summary?.totals || []).map((t) => (
                <tr key={t.user_id} style={{ borderBottom: "1px solid #e2e8f0" }}>
                  <td style={{ padding: "6px 4px", fontWeight: 600 }}>{t.user_name}</td>
                  <td style={{ padding: "6px 4px" }}>{roleLabel(t.user_role)}</td>
                  <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: "IBM Plex Mono, monospace" }}>{fmtHours(t.seconds)}</td>
                  <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: "IBM Plex Mono, monospace" }}>{t.entries}</td>
                </tr>
              ))}
              {(!summary?.totals || summary.totals.length === 0) && (
                <tr><td colSpan={4} style={{ padding: 8, color: "#64748b" }}>No hours logged this week yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Clock card */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md shadow-sm p-6 no-print">
          <div className="flex items-center gap-3">
            <div className={`grid place-items-center h-12 w-12 rounded-sm ${status?.clocked_in ? "bg-blue-800 text-white" : "bg-slate-100 text-slate-700"}`}>
              <Clock size={22} />
            </div>
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Current shift</div>
              <div className="mt-0.5 font-display text-2xl font-black tracking-tighter text-slate-900">
                {status?.clocked_in ? "On the clock" : "Ready to start"}
              </div>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="border border-slate-200 rounded-sm p-4 bg-slate-50">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Elapsed</div>
              <div className="mt-1 font-mono text-3xl font-black text-slate-900" data-testid="time-elapsed">
                {status?.clocked_in ? fmtDuration(liveElapsed) : "00:00:00"}
              </div>
              {activeEntry?.clock_in && (
                <div className="mt-2 text-xs text-slate-500 font-mono">
                  Started {fmtDateTime(activeEntry.clock_in)}
                </div>
              )}
            </div>
            <div className="border border-slate-200 rounded-sm p-4 bg-slate-50">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">This week</div>
              <div className="mt-1 font-mono text-3xl font-black text-slate-900" data-testid="time-week">
                {fmtHours(myWeekTotal + (status?.clocked_in ? liveElapsed : 0))}
              </div>
              <div className="mt-2 text-xs text-slate-500 font-mono">
                Since {summary?.week_start ? new Date(summary.week_start).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "—"}
              </div>
            </div>
          </div>

          <div className="mt-6">
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">
              {status?.clocked_in ? "Add a note (optional)" : "Starting note (optional)"}
            </Label>
            <Input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={status?.clocked_in ? "Where were you working, tasks, etc." : "e.g. Riverside site"}
              data-testid="time-note-input"
              className="mt-1.5 rounded-sm"
            />
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {status?.clocked_in ? (
              <Button
                onClick={doClockOut}
                disabled={busy}
                data-testid="clock-out-btn"
                className="h-12 px-6 rounded-sm bg-red-600 hover:bg-red-700 text-white font-semibold text-base"
              >
                <Square size={16} className="mr-2" /> Clock Out
              </Button>
            ) : (
              <Button
                onClick={doClockIn}
                disabled={busy}
                data-testid="clock-in-btn"
                className="h-12 px-6 rounded-sm bg-blue-800 hover:bg-blue-900 text-white font-semibold text-base"
              >
                <Play size={16} className="mr-2" /> Clock In
              </Button>
            )}
          </div>
        </div>

        {/* Weekly team summary (managers) or self summary */}
        <aside className="bg-white border border-slate-200 rounded-md shadow-sm p-6 no-print">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
            <UsersIcon size={14} /> {isManager ? "Team this week" : "Your week"}
          </div>
          <div className="mt-3 space-y-2" data-testid="week-totals">
            {summary?.totals?.length ? (
              summary.totals.map((t) => (
                <div key={t.user_id} className="flex items-center justify-between text-sm">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">{t.user_name}</div>
                    <div className="text-xs text-slate-500">{roleLabel(t.user_role)}</div>
                  </div>
                  <div className="font-mono font-semibold text-slate-900 ml-3 shrink-0">
                    {fmtHours(t.seconds)}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-sm text-slate-500 font-mono">No hours logged yet this week.</div>
            )}
          </div>
        </aside>
      </div>

      {/* Entries table */}
      <div className="px-4 md:px-8 pb-10">
        <div className="border border-slate-200 rounded-md overflow-x-auto bg-white shadow-sm">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-3">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">History</div>
              <div className="mt-1 font-display text-xl font-bold text-slate-900">
                <Timer size={16} className="inline -mt-1 mr-1" /> Time entries
              </div>
            </div>
            {isManager && (
              <div className="w-56 no-print">
                <Select value={viewUserId || "__all__"} onValueChange={(v) => setViewUserId(v === "__all__" ? "" : v)}>
                  <SelectTrigger className="rounded-sm" data-testid="time-user-filter">
                    <SelectValue placeholder="All team" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All team</SelectItem>
                    {users.map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <table className="w-full text-sm min-w-[720px]" data-testid="time-entries-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                {isManager && <th className="p-4">User</th>}
                <th className="p-4">Clock In</th>
                <th className="p-4">Clock Out</th>
                <th className="p-4 text-right">Duration</th>
                <th className="p-4">Note</th>
              </tr>
            </thead>
            <tbody>
              {entriesFiltered.length === 0 ? (
                <tr>
                  <td colSpan={isManager ? 5 : 4} className="p-4 md:p-8 text-center text-slate-500 font-mono text-sm">
                    No time entries yet.
                  </td>
                </tr>
              ) : entriesFiltered.map((e) => (
                <tr key={e.id} className="border-b border-slate-200 last:border-b-0" data-testid={`entry-row-${e.id}`}>
                  {isManager && <td className="p-4 text-slate-900 font-semibold">{e.user_name}</td>}
                  <td className="p-4 font-mono text-xs text-slate-700">{fmtDateTime(e.clock_in)}</td>
                  <td className="p-4 font-mono text-xs text-slate-700">
                    {e.clock_out ? fmtDateTime(e.clock_out) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-sm bg-blue-100 text-blue-800 text-[10px] font-bold uppercase tracking-widest">
                        In progress
                      </span>
                    )}
                  </td>
                  <td className="p-4 text-right font-mono font-semibold">
                    {e.clock_out ? fmtHours(e.duration_seconds) : fmtDuration(e.elapsed_seconds || 0)}
                  </td>
                  <td className="p-4 text-slate-600 text-sm">{e.note || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
